import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../redux/store';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Placeholder } from '../components/Placeholder';

import { fetchMyRechargeCredit } from '../redux/lounges/slice';
import { selectLounges } from '../redux/lounges/selectors';
import { GET_BASE_URL } from '../constants/apiEndpoints';
import {
  formatCreditAmount,
  formatCredits,
  hasRechargePackageSaleBoost,
  isRechargeSaleActive,
  type CreditSale,
  type RechargePackage,
} from '../utils/rechargePackages';
import { loadStripeJs, type StripeEmbeddedCheckout } from '../utils/stripeEmbeddedCheckout';

import { LoungeHeader } from '../components/LoungeHeader';
import { MobileLoungeHeader } from '../components/MobileLoungeHeader';

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';

const MyRecharge = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { myRechargeItem, status } = useSelector(selectLounges);

  const stripeCheckoutRef = useRef<StripeEmbeddedCheckout | null>(null);
  const stripeCheckoutContainerRef = useRef<HTMLDivElement | null>(null);
  const [selectedRechargePackage, setSelectedRechargePackage] = useState<RechargePackage | null>(null);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [rechargeError, setRechargeError] = useState('');
  const [rechargeSuccessMessage, setRechargeSuccessMessage] = useState('');
  const [postPurchaseToLounge, setPostPurchaseToLounge] = useState(false);

  let sortType: any = null;
  let currentPage: any = null;

  const rechargeData = myRechargeItem as any;
  const creditSale = (rechargeData?.credit_sale || null) as CreditSale | null;
  const isCreditSaleActive = isRechargeSaleActive(creditSale);
  const rechargePackages = useMemo(() => {
    return Array.isArray(rechargeData?.store) ? rechargeData.store : [];
  }, [rechargeData?.store]);

  const destroyStripeCheckout = useCallback(() => {
    if (stripeCheckoutRef.current) {
      stripeCheckoutRef.current.destroy();
      stripeCheckoutRef.current = null;
    }
  }, []);

  const refreshRechargeCredits = useCallback(async () => {
    await dispatch(fetchMyRechargeCredit({ sortType: null, currentPage: null } as any));
  }, [dispatch]);

  const pollRechargeStatus = useCallback(async (sessionId: string) => {
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      return;
    }

    for (let attempt = 0; attempt < 8; attempt += 1) {
      try {
        const response = await axios.get(`${GET_BASE_URL}/api/v1/stripe/credits/session/${sessionId}`, {
          headers: {
            Authorization: `bearer ${authToken}`,
          },
        });

        if (response.data?.credited) {
          await refreshRechargeCredits();
          setRechargeSuccessMessage('Payment complete. Your credits have been added.');
          setSelectedRechargePackage(null);
          destroyStripeCheckout();
          return;
        }
      } catch (error) {
        console.error('Error checking Stripe credit status:', error);
      }

      await new Promise((resolve) => setTimeout(resolve, 1500));
    }

    await refreshRechargeCredits();
    setRechargeSuccessMessage('Payment complete. Credits may take a moment to appear.');
  }, [destroyStripeCheckout, refreshRechargeCredits]);

  const startRechargeCheckout = async (creditPackage: RechargePackage) => {
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      navigate('/disneyland/login', {
        state: { returnTo: '/disneyland/recharge', source: 'RechargeCredits' },
      });
      return;
    }

    setSelectedRechargePackage(creditPackage);
    setIsStartingCheckout(true);
    setRechargeError('');
    setRechargeSuccessMessage('');
    destroyStripeCheckout();

    try {
      const response = await axios.post(
        `${GET_BASE_URL}/api/v1/stripe/credits/checkout-session`,
        {
          credit_package_id: creditPackage.id,
          return_url: `${window.location.origin}/disneyland/recharge`,
          post_to_lounge: postPurchaseToLounge,
        },
        {
          headers: {
            Authorization: `bearer ${authToken}`,
          },
        }
      );

      const { publishableKey, clientSecret, sessionId } = response.data || {};
      if (!publishableKey || !clientSecret || !sessionId) {
        throw new Error('Stripe checkout did not return a usable session.');
      }

      await loadStripeJs();
      if (!window.Stripe) {
        throw new Error('Stripe.js did not load.');
      }

      const stripe = window.Stripe(publishableKey);
      const checkout = await stripe.initEmbeddedCheckout({
        fetchClientSecret: async () => clientSecret,
        onComplete: () => {
          pollRechargeStatus(sessionId).catch((error) => {
            console.error('Error refreshing credits after Stripe checkout:', error);
            setRechargeSuccessMessage('Payment complete. Credits may take a moment to appear.');
          });
        },
      });

      stripeCheckoutRef.current = checkout;
      if (stripeCheckoutContainerRef.current) {
        checkout.mount(stripeCheckoutContainerRef.current);
      }
    } catch (error) {
      console.error('Error starting Stripe checkout:', error);
      setRechargeError(error instanceof Error ? error.message : 'Unable to start Stripe checkout.');
      setSelectedRechargePackage(null);
    } finally {
      setIsStartingCheckout(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    dispatch(fetchMyRechargeCredit({ sortType, currentPage }));
  }, [dispatch]);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('stripe_session_id');
    if (!sessionId) {
      return;
    }

    pollRechargeStatus(sessionId);
    window.history.replaceState({}, document.title, window.location.pathname);
  }, [pollRechargeStatus]);

  useEffect(() => {
    return () => {
      destroyStripeCheckout();
    };
  }, [destroyStripeCheckout]);

  return (
    <>
      <div className='mid-main'>
        <div className='container'>
          <div className='mid-sec mwstore-page-bg'>
            <LoungeHeader />
            <MobileLoungeHeader />
            {/*-=====mobile-view start ======-*/}
            <div className='top-text-m text-center p-2 text-head text-center'>
              <div className='top-mw text-center'>
                <h6>Top MouseWaiters of the Day</h6>
                <div className='top-sp d-flex justify-content-center'>
                  <p>
                    <span>1. grumpypapa 56.38 #5 (DL)</span>
                    <span>2. grapesoda 52.67 #5 (DL)</span>
                  </p>
                </div>
              </div>
              <div className='text-head-m text-center'>
                <ul className='p-0 m-0'>
                  <li>
                    <a href=''>Best of the Day</a>
                  </li>
                  <li>
                    <a href=''>Food Blog</a>
                  </li>
                  <li>
                    <a href=''>Tips</a>
                  </li>
                  <li>
                    <a href=''>Sticker Store</a>
                  </li>
                </ul>
              </div>
              <div className='Search-bar'>
                <div className='input-group  bg-white border rounded-pill p-1'>
                  <input
                    type='search '
                    placeholder='Search the Lounge'
                    aria-describedby='button-addon4'
                    className='form-control  border-0'
                  />
                  <div className='input-group-prepend border-0'>
                    <button id='button-addon4' type='button' className='btn'>
                      <i className='fa fa-search' />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className='mwstore-mid-bg'>
              <Tabs style={{ borderTopWidth: 0 }}>
                <TabList>
                  <Tab>
                    {' '}
                    <Link to='/disneyland/mystore'> MW Store </Link>{' '}
                  </Tab>
                  <Tab>
                    {' '}
                    <Link to='/disneyland/recharge'>
                      {' '}
                      Recharge Credits{' '}
                    </Link>{' '}
                  </Tab>
                  <Tab>
                    {' '}
                    <Link to='/disneyland/mycollection'>
                      {' '}
                      My Collection{' '}
                    </Link>{' '}
                  </Tab>
                  <Tab>
                    {' '}
                    <Link to='/disneyland/mytrade-request'>
                      {' '}
                      My Trade Request{' '}
                    </Link>{' '}
                  </Tab>
                  <Tab>
                    {' '}
                    <Link to='/disneyland/myhistory'> My History </Link>{' '}
                  </Tab>
                  <Tab>
                    {' '}
                    <Link to='/disneyland/giftcredits'>Gift Credits </Link>{' '}
                  </Tab>
                </TabList>

                <TabPanel>
                  <div className='MW-buy-sec'>
                    <div className='container'>
                      <div className='row'>
                        <div className='buy-m'>
                          <div className='mws-mid-sec mb-4'>
                            <h5 className='mid-head'>Recharge Credits</h5>
                            <h6
                              className='mid-sub'
                              style={{ fontWeight: '600' }}
                            >
                              <span>
                                Credit Balance :{' '}
                                {formatCredits(rechargeData?.credit_balance || 0)}
                              </span>
                            </h6>
                          </div>
                          {rechargeError ? (
                            <p className='mw-recharge-feedback is-error'>{rechargeError}</p>
                          ) : null}
                          {rechargeSuccessMessage ? (
                            <p className='mw-recharge-feedback is-success'>{rechargeSuccessMessage}</p>
                          ) : null}
                          {isCreditSaleActive ? (
                            <p className='mw-recharge-feedback is-success'>
                              {creditSale?.label || `${formatCredits(creditSale?.multiplier || 1)}x credits sale`} is active.
                              Buy credits at the regular price and receive the boosted amount shown below.
                            </p>
                          ) : null}
                          <label className='mw-purchase-share-option'>
                            <input
                              type='checkbox'
                              checked={postPurchaseToLounge}
                              onChange={(event) => setPostPurchaseToLounge(event.target.checked)}
                            />
                            <span>
                              <strong>Post my purchase to the Lounge</strong>
                              <small>
                                Kate (Virtual Plaid) will post a thank-you status after payment.
                              </small>
                            </span>
                          </label>

                          {status === 'loading'
                            ? [...new Array(9)]?.map((_, index) => (
                                <Placeholder key={index} />
                              ))
                            : (
                              <div className='mw-recharge-list'>
                                {rechargePackages.map((obj: RechargePackage) => {
                                  const isSelected = selectedRechargePackage?.id === obj.id;
                                  const isThisStarting = isStartingCheckout && isSelected;
                                  const hasSaleBoost = hasRechargePackageSaleBoost(obj, creditSale);
                                  const baseCredits = obj.base_credits ?? obj.credits;

                                  return (
                                    <div
                                      className={`mw-recharge-card${isSelected ? ' is-selected' : ''}`}
                                      key={obj.id}
                                    >
                                      <div className='mw-recharge-card__main'>
                                        <div className='mw-recharge-card__copy'>
                                          <h6>Buy {formatCredits(obj.credits)} Credits</h6>
                                          {hasSaleBoost ? (
                                            <small>
                                              Regular package: {formatCredits(baseCredits)} credits. Sale bonus included.
                                            </small>
                                          ) : null}
                                          <span>{formatCreditAmount(obj.amount)}</span>
                                        </div>
                                        <button
                                          type='button'
                                          className='mw-recharge-card__button'
                                          onClick={() => startRechargeCheckout(obj)}
                                          disabled={isStartingCheckout}
                                        >
                                          {isThisStarting ? 'Starting checkout...' : 'Buy with Stripe'}
                                        </button>
                                      </div>
                                      {isSelected ? (
                                        <div className='mw-recharge-checkout' aria-live='polite'>
                                          <div className='mw-recharge-checkout__header'>
                                            <div>
                                              <h6>
                                                Checkout: {formatCredits(obj.credits)} Credits
                                              </h6>
                                              {hasSaleBoost ? (
                                                <small>
                                                  Regular package: {formatCredits(baseCredits)} credits. Sale bonus included.
                                                </small>
                                              ) : null}
                                              <span>{formatCreditAmount(obj.amount)}</span>
                                            </div>
                                            <button
                                              type='button'
                                              onClick={() => {
                                                destroyStripeCheckout();
                                                setSelectedRechargePackage(null);
                                              }}
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                          <div
                                            ref={stripeCheckoutContainerRef}
                                            className='mw-recharge-checkout__stripe'
                                          />
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                        </div>
                        <div></div>
                      </div>
                    </div>
                  </div>
                </TabPanel>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MyRecharge;
